import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchSelect } from '../../components/search-select.js';

// #195 (W4): Leiste am Fuß der Playlist-Spalte — „N ausgewählt · Weitergeben an [Kollege ▾]
// [Weitergeben]". Sie erscheint erst ab einer Markierung (das entscheidet die Playlist) und
// passt in die 360px-Spalte: oben Zähler + „Auswahl aufheben", darunter Auswahl + Schaltfläche.
// Sie klebt (`sticky bottom-0`) am unteren Rand des sichtbaren Bereichs, weil die Seite bei vielen
// Karten scrollt und die Leiste sonst unterhalb des Viewports läge. Das greift nur, solange kein
// Vorfahre ein Scroll-Container ist: die Zeile in my-tests-page.tsx nutzt deshalb `overflow-clip`
// (clippt wie `hidden`, bildet aber keinen Scroll-Container, an dem `sticky` hängen bliebe).
//
// `targets` ist bereits die Schnittmenge aller markierten Testfälle (commonTargets) mit Namen und
// der Zahl seiner übernehmbaren Einträge. Die Leiste kennt keine Regeln, sie zeigt nur.
export interface HandoverTarget {
  user_id: string;
  name: string;
  /** Seine weitergebbaren Einträge — „Name · n offen". */
  open: number;
}

export function HandoverBar({
  count,
  targets,
  pending,
  onGive,
  onClear,
}: {
  /** Markierte EINTRÄGE (Testfall × Kette), nicht Testfälle — Zählregel aus CONTEXT.md. */
  count: number;
  targets: HandoverTarget[];
  pending: boolean;
  onGive: (toUserId: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState('');
  // Eine Wahl, die nach einer Änderung der Markierung kein gemeinsames Ziel mehr ist, gilt nicht
  // mehr (sonst ginge der Test an jemanden, der ihn gar nicht bekommen darf — der Server würde ihn
  // zwar abweisen, aber der Tester sähe das Ziel noch gewählt). Ohne Wahl nur dann vorbelegt, wenn
  // es genau ein Ziel gibt: „Weitergeben" wirkt sofort und ohne Rückfrage, bei mehreren Zielen
  // soll die Person bewusst gewählt sein.
  const value = targets.some((target) => target.user_id === chosen)
    ? chosen
    : targets.length === 1
      ? (targets[0]?.user_id ?? '')
      : '';
  const noTarget = targets.length === 0;

  return (
    <div
      data-handover-bar
      className="sticky bottom-0 z-10 flex flex-col gap-2 border-t border-[var(--border)] bg-bg-surface p-3"
    >
      <div className="flex items-center justify-between gap-2 text-xs">
        <b className="text-text-primary">{t('handover.selected', { count })}</b>
        <button type="button" onClick={onClear} className="text-brand-accent hover:underline">
          {t('handover.clear')}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <SearchSelect
            label={t('handover.giveTo')}
            value={value}
            disabled={noTarget}
            onChange={setChosen}
            options={targets.map((target) => ({
              value: target.user_id,
              label: t('handover.targetOption', { name: target.name, count: target.open }),
            }))}
            emptyLabel={t('handover.giveTo')}
            className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-body disabled:opacity-60"
          />
        </div>
        <button
          type="button"
          disabled={noTarget || value === '' || pending}
          onClick={() => onGive(value)}
          className="shrink-0 rounded-btn bg-brand-accent px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
        >
          {t('handover.give')}
        </button>
      </div>
      {noTarget && (
        <span className="text-[11px] text-text-muted">{t('handover.noCommonTarget')}</span>
      )}
    </div>
  );
}
